import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import steveImage from '../assets/SteveJobs.webp';
import { FaBriefcase, FaGraduationCap, FaCode } from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";

const timeline = [
  {
    title: "Freelancer",
    company: "Self-employed",
    location: "Remote",
    date: "March 2026 – Present",
    type: "work",
    description: "Since going independent, I've been building full-stack web apps, CRMs, and business automations for clients remotely. I take projects end to end, from the first conversation about what a business actually needs, through design, build, and deployment. Most of my work is in React, Next.js, and Laravel, with a growing focus on AI integrations and workflow automation.",
    skills: ["Next.js", "TypeScript", "Supabase", "AI Integration", "n8n", "Zapier", "Make", "GoHighLevel", "Stripe", "Client Communication"],
  },
  {
    title: "Software Engineer",
    company: "Park Cabins",
    location: "Sydney, Australia (Remote)",
    date: "June 2025 – March 2026",
    type: "work",
    description: "I was the sole developer on a production CRM for an Australian cabin construction company, built over nine months and hosted on AWS. I took it from nothing to a live system with 20+ REST API endpoints and over a thousand booking records, replacing a fully manual operations process end to end. Working solo meant I owned all of it: the architecture, the database design, the API, the interface, and deployment.",
    skills: ["Laravel", "React", "MySQL", "REST APIs", "AWS", "System Architecture", "Solo Ownership"],
  },
  {
    title: "Graduated as Magna Cum Laude",
    company: "West Visayas State University",
    location: "Iloilo City, Iloilo, Philippines",
    date: "May 2025",
    type: "education",
    description: "I finished my BS in Information Technology (Software Technology track) with Magna Cum Laude honors. Four years of fundamentals, projects, and long nights that turned early curiosity into a real engineering foundation.",
    skills: ["Software Engineering Fundamentals", "Full-Stack Development", "Capstone Delivery"],
  },
  {
    title: "Internship",
    company: "Meralco PowerGen",
    location: "Iloilo, Iloilo, Philippines",
    date: "February 2025",
    type: "work",
    description: "At Meralco PowerGen, I rotated across IT operations, configuring switches and VLANs and doing cable crimping, and ran vulnerability assessments and penetration testing on internal systems. I also handled admin support like onboarding access and company email provisioning, and monitored company websites and systems, logging status and reporting to my supervisor. I also worked with company data to forecast trends and support decision-making.",
    skills: ["Networking (VLANs)", "VAPT", "IT Admin Support", "Systems Monitoring", "Data Analysis & Forecasting"],
  },
  {
    title: "BS in Information Technology",
    company: "West Visayas State University",
    location: "Iloilo City, Iloilo, Philippines",
    date: "August 2021",
    type: "education",
    description: "This is where the formal journey began. I started my degree in Information Technology, drawn to the idea that I could build things people actually use, and spent the next four years turning that pull into real skills.",
    skills: ["OOP & Data Structures", "SQL & Databases", "HTML/CSS/JS", "Problem Solving"],
  },
  {
    title: "Hello World",
    company: "Wrote my first line of code.",
    date: "August 2019",
    type: "milestone",
    description: "The very beginning. I wrote my first line of code out of pure curiosity, watched it run, and got hooked on the feeling of making a machine do exactly what I told it to. Everything after this point grew from that one small moment.",
    skills: ["First Programming Language", "Problem-Solving Mindset", "Self-Learning"],
  },
];

const Experience = ({ embed = false }) => {
  const navigate = useNavigate();

  // Home restores the exact scroll position the user left from (tracked in
  // sessionStorage), so Back just needs to land back on "/".
  const goBack = () => navigate("/");

  return (
    <>
      {!embed && (
        <div
          style={{ background: "#FFFFFF" }}
          className="pt-8 md:pt-10 pb-4 md:pb-6 px-4"
        >
          <div className="max-w-3xl mx-auto w-full">
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-brand/60 hover:text-brand transition mb-6 text-sm font-semibold tracking-[0.15em] uppercase"
            >
              <FiArrowLeft size={15} />
              Back
            </button>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-3xl mx-auto w-full text-center"
          >
            <p className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-brand/60 mb-2">
              Timeline
            </p>
            <h1
              style={{ fontFamily: "DM Sans, sans-serif", letterSpacing: "clamp(-1px, -0.2vw, -2px)", fontSize: "clamp(24px, 4vw, 36px)" }}
              className="text-brand-800 font-extrabold"
            >
              My Journey
            </h1>
            <p className="mt-2 text-brand-700/70 text-sm md:text-base leading-relaxed">
              A look back at the experiences, education, and milestones that shaped me into the engineer I am today.
            </p>
          </motion.div>
        </div>
      )}
      <section className={embed ? "pt-0 pb-0" : "pt-4 pb-16 px-4 max-w-3xl mx-auto"} id="experience">
        {embed ? (
          <div className="relative">
            {/* rail runs through the center of the 10px dot column */}
            <div className="absolute top-0 bottom-0 w-px" style={{ left: "4px", background: "linear-gradient(to bottom, #111111 80%, transparent)", opacity: 0.12 }} />
            {timeline.slice(0, 4).map((item, index) => (
              <motion.div
                key={index}
                className="flex gap-5 pb-7"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
                viewport={{ once: true }}
              >
                {/* dot — 8px wide, centered in this column so rail bisects it */}
                <div className="flex-shrink-0 flex items-start justify-center" style={{ width: "9px", paddingTop: "5px" }}>
                  <div className="w-2 h-2 rounded-full bg-[#111111]" />
                </div>
                {/* content */}
                <div>
                  <p style={{ fontSize: "12px", color: "#11111199", letterSpacing: "0.08em", textTransform: "uppercase" }} className="font-medium mb-0.5">{item.date}</p>
                  <h4 style={{ fontFamily: "DM Sans, sans-serif", fontSize: "14px", fontWeight: 700, color: "#111111", letterSpacing: "-0.2px" }} className="leading-snug mb-0.5">{item.title}</h4>
                  <p style={{ fontSize: "12px", color: "#555555" }}>{item.company}</p>
                  {item.location && <p style={{ fontSize: "11px", color: "#999999" }}>{item.location}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="relative">
            <div className="absolute top-0 bottom-0 w-px" style={{ left: "19px", background: "linear-gradient(to bottom, #111111 80%, transparent)", opacity: 0.12 }} />
            {timeline.map((item, index) => {
              const getIcon = () => {
                if (item.type === "work") return <FaBriefcase size={13} />;
                if (item.type === "education") return <FaGraduationCap size={13} />;
                return <FaCode size={13} />;
              };
              return (
                <motion.div
                  key={index}
                  className="flex gap-4 pb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  viewport={{ once: true }}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#111111] flex items-center justify-center text-white z-10 shadow-sm">
                    {getIcon()}
                  </div>
                  <div className="flex-1 pt-1">
                    <p style={{ fontSize: "12px", color: "#11111166", letterSpacing: "0.06em", textTransform: "uppercase" }} className="font-medium mb-1">{item.date}</p>
                    <h4 style={{ fontFamily: "DM Sans, sans-serif", fontSize: "18px", fontWeight: 700, color: "#111111", letterSpacing: "-0.3px" }} className="leading-snug mb-0.5">{item.title}</h4>
                    <p style={{ fontSize: "14px", color: "#555555" }} className="font-medium">{item.company}</p>
                    {item.location && <p style={{ fontSize: "13px", color: "#888888" }} className="mt-0.5">{item.location}</p>}
                    {item.description && <p style={{ fontSize: "14px", color: "#666666" }} className="mt-2 leading-relaxed">{item.description}</p>}
                    {item.skills && item.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {item.skills.map((skill) => (
                          <span
                            key={skill}
                            className="inline-flex items-center py-1 px-2.5 text-xs font-medium"
                            style={{ backgroundColor: "#F0F0F0", color: "#1C1C1E" }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

    {/* Quote hidden for now — change false to true to restore */}
    {false && (
    <section style={{ backgroundColor: "#FAFAFA" }} className="w-full py-20 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="flex flex-col items-center text-center"
      >
        {/* Quote mark */}
        <span className="text-8xl leading-none select-none" style={{ fontFamily: "Forum, serif", color: "#D4D4D4" }}>&ldquo;</span>

        {/* Quote text */}
        <h4
          className="text-2xl md:text-3xl mt-2 mb-10 max-w-2xl"
          style={{ fontFamily: "Forum, serif", color: "#111111" }}
        >
          The only way to do great work is to love what you do
        </h4>

        {/* Avatar circle */}
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 mb-3" style={{ borderColor: "#D4D4D4" }}>
          <img
            src={steveImage}
            alt="Steve Jobs"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>

        {/* Name */}
        <p style={{ fontSize: 14, color: "#737373" }}>Steve Jobs</p>
      </motion.div>
    </section>
    )}
    </>
  );
};

export default Experience;
